import { Box, Button, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MenuIcon from '@mui/icons-material/Menu'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import NotificationsBell from './NotificationsBell'
import { useEdition } from '@/hooks/useEdition'
import { supportMailto } from '@/config/contact'
import FeedbackDialog from './FeedbackDialog'
import GlobalSearch from './GlobalSearch'
import { useSubscription } from '@/hooks/useSubscription'
import { useNavigate } from 'react-router-dom'

type ResearcherTopBarProps = {
  crumbs: string[]
  onPrimaryAction?: () => void
  hideAction?: boolean
  /** Opens the mobile nav drawer (hamburger shown below the `md` breakpoint). */
  onMenuClick?: () => void
}

export default function ResearcherTopBar({ crumbs, onPrimaryAction, hideAction = false, onMenuClick }: ResearcherTopBarProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { isExpired } = useSubscription()
  const { isPro } = useEdition()

  const [helpAnchor, setHelpAnchor] = useState<null | HTMLElement>(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const helpOpen = Boolean(helpAnchor)

  const iconBtnSx = {
    width: 36,
    height: 36,
    borderRadius: '12px',
    bgcolor: c.gray50,
    color: c.ink72,
    '&:hover': { bgcolor: c.gray100 },
  }

  return (
    <Box
      component="header"
      sx={{
        height: 60,
        flex: '0 0 60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, md: 3 },
        gap: { xs: 1, md: 3 },
        bgcolor: c.white,
        borderBottom: `1px solid ${c.ink08}`,
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
        <IconButton
          aria-label={t('topbar.openMenu')}
          onClick={onMenuClick}
          sx={{ ...iconBtnSx, display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }}
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box
          component="nav"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, minWidth: 0, overflow: 'hidden' }}
        >
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1
          return (
            <Fragment key={i}>
              {i > 0 && (
                <ChevronRightIcon sx={{ fontSize: 14, color: c.ink32, display: { xs: 'none', sm: 'inline' } }} />
              )}
              <Box
                component="span"
                sx={{
                  color: isLast ? c.ink : c.ink56,
                  fontWeight: isLast ? 700 : 400,
                  whiteSpace: 'nowrap',
                  // On mobile hide all but the last crumb, and let it truncate.
                  display: !isLast ? { xs: 'none', sm: 'inline' } : 'inline',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                {crumb}
              </Box>
            </Fragment>
          )
        })}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <GlobalSearch />

        <LanguageSwitcher />
        <IconButton
          aria-label={t('topbar.help')}
          aria-haspopup="true"
          aria-expanded={helpOpen ? 'true' : undefined}
          onClick={(e) => setHelpAnchor(e.currentTarget)}
          sx={iconBtnSx}
        >
          <HelpOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Menu
          anchorEl={helpAnchor}
          open={helpOpen}
          onClose={() => setHelpAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 1, minWidth: 200, borderRadius: 2 } } }}
        >
          <MenuItem
            component="a"
            href="/docs/instrukcja_facewise.pdf"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setHelpAnchor(null)}
          >
            <ListItemIcon>
              <MenuBookOutlinedIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText>{t('helpMenu.manual')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setHelpAnchor(null)
              setFeedbackOpen(true)
            }}
          >
            <ListItemIcon>
              <FeedbackOutlinedIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText>{t('helpMenu.feedback')}</ListItemText>
          </MenuItem>
          {/* Kanał wsparcia obiecany w regulaminie §11.4. Formularz opinii obok
              idzie na skrzynkę produktową i nie jest zgłoszeniem problemu —
              ktoś, komu nie przetwarza się nagranie, potrzebuje adresu, pod
              którym ktoś odpisze. */}
          <MenuItem
            component="a"
            href={supportMailto(t('helpMenu.contactMailSubject'))}
            onClick={() => setHelpAnchor(null)}
          >
            <ListItemIcon>
              <MailOutlineIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText>{t('helpMenu.contact')}</ListItemText>
          </MenuItem>
        </Menu>
        <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        {/* The bell is the activity feed in miniature — events from across a
            researcher's studies — so it belongs to the same research-programme
            instrument FaceWise does without. Its endpoint refuses a FaceWise
            account, and a bell that can only ever say "could not load" is worse
            than no bell. */}
        {isPro && <NotificationsBell iconBtnSx={iconBtnSx} />}

        {!hideAction && (
          <Tooltip title={isExpired ? t('trial.blockedTooltip') : ''}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={isExpired ? () => navigate('/plans') : onPrimaryAction}
              >
                {t('topbar.newStudy')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}
